<template>
  <div class="tab__container">
    <p>
      <i class="fa fa-times-circle text-error"></i>
      <b class="ml-2">¿No está en tu escuela?</b>
    </p>

    <ValidationObserver ref="form">
      <ValidationProvider v-slot="{ errors }" rules="required">
        <CustomDropDown
          v-model="model.reason"
          :options="reasonOptions"
          class="w-35 my-4"
          :disabled="disabled"
          :error="errors[0]"
          fieldname="name"
          fieldkey="id"
          custom-error-msg="Favor seleccionar una razón."
          label="Razón:"
          placeholder="Seleccione"
        />
      </ValidationProvider>

      <div v-if="isOtherSelected">
        <ValidationProvider v-slot="{ errors }" rules="required">
          <CustomTextArea
            v-model="model.comment"
            :placeholder="'Escribe un comentario'"
            :disabled="disabled"
            class="my-4"
            :maxlength="3000"
            :rows="5"
            custom-error-msg="Favor de registrar un comentario."
            :error="errors[0]"
          />
        </ValidationProvider>
      </div>
    </ValidationObserver>

    <div class="action-buttons__container" v-if="!disabled">
      <action-button
        variant="outline-danger"
        i="fa fa-times-circle"
        text="Cancelar"
        class="mr-2"
        @click="$emit('cancel')"
      />
      <action-button
        variant="success"
        i="fas fa-save"
        text="Guardar"
        @click="save"
      />
    </div>
  </div>
</template>

<script>
import IconView from 'icon-view';
import CustomDropDown from 'custom-drop-down';
import CustomTextArea from 'CustomTextArea';
import ActionButton from 'ActionButton';
import IsNotInMySchoolReasons from '@/utils/constants/IsNotInMySchoolReasons';
import alert from '@/mixin/alert.mixin';
import { validateIsNotInMySchoolAction } from '@/modules/human-resources/api';

export default {
  name: 'IsNotInMySchool',
  mixins: [alert],
  components: {
    IconView,
    CustomDropDown,
    CustomTextArea,
    ActionButton,
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        reason: null,
        comment: null,
      }),
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    reasonOptions: {
      type: Array,
      default: () => [],
    },
    assistantId: {
      type: [Number, String],
      default: null,
    },
    schoolId: {
      type: [Number, String],
      default: null,
    },
    academicYearId: {
      type: [Number, String],
      default: null,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      model: {
        reason: null,
        comment: null,
      },
      isValidationOk: false,
      isValidationLoading: false,
      lastValidationContext: null,
      lastValidationResult: null,
      pendingValidationPromise: null,
    };
  },
  computed: {
    isOtherSelected() {
      return this.model.reason?.id == IsNotInMySchoolReasons.Others;
    },
    hasValidationContext() {
      return [this.assistantId, this.schoolId, this.academicYearId].every(
        (value) => value !== null && value !== undefined && value !== ''
      );
    },
    validationContextKey() {
      if (!this.hasValidationContext) return null;
      return `${this.assistantId}|${this.schoolId}|${this.academicYearId}`;
    },
  },
  methods: {
    invalidateValidationCache() {
      this.lastValidationContext = null;
      this.lastValidationResult = null;
      this.isValidationOk = false;
    },
    async validateIsNotInMySchool(showError = true, force = false) {
      if (!this.hasValidationContext || this.disabled) {
        this.isValidationOk = false;
        return false;
      }

      const currentContext = this.validationContextKey;

      if (
        !force &&
        this.lastValidationContext === currentContext &&
        this.lastValidationResult !== null
      ) {
        this.isValidationOk = this.lastValidationResult;
        return this.lastValidationResult;
      }

      if (this.isValidationLoading && this.pendingValidationPromise) {
        return this.pendingValidationPromise;
      }

      this.isValidationLoading = true;
      this.isValidationOk = false;

      this.pendingValidationPromise = (async () => {
        try {
          const { data } = await validateIsNotInMySchoolAction(
            this.assistantId,
            this.schoolId,
            this.academicYearId
          );
          this.isValidationOk = !!data;
          this.lastValidationContext = currentContext;
          this.lastValidationResult = this.isValidationOk;
          return this.isValidationOk;
        } catch (error) {
          this.isValidationOk = false;
          this.lastValidationContext = currentContext;
          this.lastValidationResult = false;
          if (showError) {
            const message = error.response?.data?.message || error.message;
            this.ShowToast('Error', message, 'error');
          }
          return false;
        } finally {
          this.isValidationLoading = false;
          this.pendingValidationPromise = null;
        }
      })();

      return this.pendingValidationPromise;
    },
    async save() {
      const canSave = await this.validateIsNotInMySchool(true, true);
      if (!canSave) return;

      const isValid = await this.$refs.form.validate();
      if (!isValid) return;

      const payload = {
        reasonId: this.model.reason?.id,
        comment: this.isOtherSelected ? this.model.comment : null,
      };
      this.$emit('save', payload);
    },
  },
  watch: {
    value: {
      deep: true,
      immediate: true,
      handler(newValue) {
        Object.assign(this.model, newValue);
      },
    },
    model: {
      deep: true,
      handler(newValue) {
        this.$emit('input', newValue);
      },
    },
    validationContextKey() {
      this.invalidateValidationCache();

      if (this.isActive) {
        this.validateIsNotInMySchool(true, false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.tab__container {
  margin: 10px;
  padding: 20px 30px;
  background-color: white;
}

.action-buttons__container {
  display: flex;
  justify-content: end;
}
</style>
